// react
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Sentry from '@sentry/react';

// hooks
import { useDocumentUpload } from './useDocumentUpload';

// store
import { newToast } from 'store/slices/uiSlice';

// types
import type { ActionAttachment } from '@karehero/llama/pwa/molecules/ActionAttachments/ActionAttachments';

interface ActionUpload {
  /** Stable row id — the file uploads under this before its document id exists. */
  id: string;
  /** The storage document id, once the upload has confirmed. */
  documentId: string | null;
  name: string;
  sizeBytes: number;
  isUploading: boolean;
}

/**
 * useActionUploads tracks the documents attached while creating/editing an
 * action. It composes useDocumentUpload for the storage work and owns the row
 * state: files upload immediately on pick (so the bytes are in GCS), and the
 * caller links the resulting `documentIds` to the entry at submit time.
 */
export const useActionUploads = (careCircleId: string) => {
  const dispatch = useDispatch();
  const { uploadDocument, removeUpload: removeDocument } =
    useDocumentUpload(careCircleId);
  const [uploads, setUploads] = useState<ActionUpload[]>([]);
  const nextRowId = useRef(0);

  const uploadFile = useCallback(
    async (file: File) => {
      const id = `upload-${nextRowId.current++}`;
      setUploads((prev) => [
        ...prev,
        {
          id,
          documentId: null,
          name: file.name,
          sizeBytes: file.size,
          isUploading: true,
        },
      ]);

      try {
        const documentId = await uploadDocument(file);
        setUploads((prev) =>
          prev.map((u) =>
            u.id === id ? { ...u, documentId, isUploading: false } : u,
          ),
        );
      } catch (error) {
        setUploads((prev) => prev.filter((u) => u.id !== id));
        Sentry.captureException(error);
        dispatch(
          newToast({
            title: 'Upload failed',
            description: 'Please try uploading the document again.',
          }),
        );
      }
    },
    [uploadDocument, dispatch],
  );

  // Open a native file picker and upload the chosen file.
  const openFilePicker = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) uploadFile(file);
    };
    input.click();
  }, [uploadFile]);

  // Remove a pending row, deleting the now-orphaned document if it confirmed.
  const removeUpload = useCallback(
    (id: string) => {
      const row = uploads.find((u) => u.id === id);
      setUploads((prev) => prev.filter((u) => u.id !== id));
      if (row?.documentId) {
        removeDocument(row.documentId);
      }
    },
    [uploads, removeDocument],
  );

  const reset = useCallback(() => setUploads([]), []);

  const attachments = useMemo<ActionAttachment[]>(
    () =>
      uploads.map((u) => ({
        id: u.id,
        name: u.name,
        meta: u.isUploading
          ? `${Math.round(u.sizeBytes / 1024)}kb · Loading`
          : undefined,
        isUploading: u.isUploading,
      })),
    [uploads],
  );

  // Confirmed (fully uploaded) ids — what the caller links to the entry.
  const documentIds = useMemo(
    () =>
      uploads
        .filter((u) => !u.isUploading && u.documentId)
        .map((u) => u.documentId as string),
    [uploads],
  );

  const isUploading = uploads.some((u) => u.isUploading);

  return {
    attachments,
    documentIds,
    isUploading,
    openFilePicker,
    removeUpload,
    reset,
  };
};
